/**
 *
 * SelectDate
 *
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import cs from 'classnames';
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import es from 'date-fns/locale/es';

import * as COLORS from 'Constants/colors';
import { InputErrorMessageProps } from 'Components/InputErrorMessage';
import { CustomSelect } from '../NewSelect';
import { formatIso, formatSuperLong, parseIso } from 'Utils/dates';
registerLocale('es', es);
setDefaultLocale('es');

interface SelectDateProps {
  selectedValue: string;
  onChange: (e: any) => void;
  placeholder?: string;
  error: InputErrorMessageProps;
  className?: string;
  datePrices?: Record<string, { amount: number; currency: string }>;
}

const formatCalendarPrice = ({ amount, currency }) => {
  if (currency === 'USD' && amount < 100000) {
    return `${amount}`;
  }

  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(amount >= 10000000 ? 0 : 1)}M`;
  }

  if (amount >= 1000) {
    return `${Math.round(amount / 1000)}k`;
  }

  return `${amount}`;
};

const SelectDate = ({
  className,
  selectedValue,
  onChange,
  placeholder = 'Elegí tu fecha de viaje',
  error,
  datePrices
}: SelectDateProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = e => {
    if (e && e.key !== 'Tab') e.preventDefault();
    setIsOpen(!isOpen);
  };

  const close = () => setIsOpen(false);

  const chooseValue = date => {
    onChange(formatIso(date));
    setIsOpen(false);
  };

  const renderDayContents = (day, date) => {
    const price = datePrices && datePrices[formatIso(date)];

    return (
      <div className={cs('calendar-day-content', { 'has-price': price })}>
        <span className="calendar-day-number">{day}</span>
        {price && (
          <span className="calendar-day-price">
            {formatCalendarPrice(price)}
          </span>
        )}
      </div>
    );
  };

  return (
    <CustomSelect
      className={cs(className)}
      label={placeholder}
      toggleOpen={toggleOpen}
      isOpen={isOpen}
      close={close}
      color="orange"
      error={error}
      showValue={selectedValue && formatSuperLong(selectedValue)}>
      <DatePicker
        selected={selectedValue && parseIso(selectedValue)}
        inline
        calendarContainer={Container}
        onChange={chooseValue}
        minDate={new Date()}
        showDisabledMonthNavigation
        renderDayContents={renderDayContents}
      />
    </CustomSelect>
  );
};

const Container = ({ children }: { children: React.ReactNode }) => (
  <div className="date-picker-holder">{children}</div>
);

export default styled(SelectDate)`
  .react-datepicker {
    font-family: Montserrat, serif;
  }

  .react-datepicker__current-month,
  .react-datepicker-time__header,
  .react-datepicker__day,
  .react-datepicker-year-header {
    font-family: Montserrat, serif;
    line-height: 0.86;
    letter-spacing: 0.18px;
    color: ${COLORS.important};
  }

  .react-datepicker__day--selected {
    background-color: ${COLORS.important};
    color: ${COLORS.white};
  }

  .react-datepicker__day--disabled {
    color: ${COLORS.darkGrey};
  }

  .react-datepicker__week,
  .react-datepicker__day-names {
    display: flex;
    margin-bottom: 5px;
    justify-content: space-around;
  }

  .react-datepicker__day,
  .react-datepicker__day-name {
    display: flex;
    width: 40px;
    height: 32px;
    justify-content: center;
    align-items: center;
  }

  .react-datepicker__day-name {
    height: 20px;
  }

  .calendar-day-content {
    align-items: center;
    display: flex;
    flex-direction: column;
    height: 30px;
    justify-content: center;
    line-height: 1;
    width: 40px;
  }

  .calendar-day-number {
    line-height: 14px;
  }

  .calendar-day-price {
    color: ${COLORS.secondary};
    font-size: 8px;
    font-weight: 700;
    line-height: 10px;
    margin-top: 2px;
    max-width: 38px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .react-datepicker__day:hover {
    border-radius: 5px;
    background-color: ${COLORS.arena};
    cursor: pointer;
  }

  .react-datepicker__day--selected .calendar-day-price {
    color: ${COLORS.white};
  }

  .react-datepicker__navigation--next,
  .react-datepicker__navigation--next:hover {
    border-left-color: ${COLORS.important};
  }

  .react-datepicker__navigation--previous--disabled,
  .react-datepicker__navigation--previous--disabled:hover {
    border-right-color: ${COLORS.darkGrey} !important;
  }

  .react-datepicker__navigation--previous,
  .react-datepicker__navigation--previous:hover {
    border-right-color: ${COLORS.important};
  }

  .react-datepicker__header {
    border: 0;
    background-color: ${COLORS.white};
  }
  .react-datepicker__month-container {
    width: 100%;
  }
`;
